import { z } from 'zod';

import { moneyInputSchema, moneySchema, optionalText, timestampSchema } from '@/types/schemas';

// --- API responses --------------------------------------------------------------------

/**
 * A membership plan runs for months, or — a day pass — for days: exactly one is set. A day pass
 * priced ₹0 is a free trial, which the API lets a member take once.
 */
export const membershipPackageSchema = z.object({
  id: z.number(),
  name: z.string(),
  durationMonths: z.number().nullable(),
  durationDays: z.number().nullable(),
  price: moneySchema,
  /** Total days one membership of this plan may be frozen; 0 = never (always 0 for a day pass). */
  maxFreezeDays: z.number(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type MembershipPackage = z.infer<typeof membershipPackageSchema>;

/** PT is sold by the month, like a membership plan — the gym does not count sessions. */
export const ptPackageSchema = z.object({
  id: z.number(),
  name: z.string(),
  durationMonths: z.number(),
  price: moneySchema,
  description: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type PtPackage = z.infer<typeof ptPackageSchema>;

// --- Forms (same limits as the API) ------------------------------------------------------

function wholeNumber(label: string, min: number, max: number) {
  return z
    .string()
    .trim()
    .regex(/^\d+$/, `Enter ${label} as a whole number`)
    .transform(Number)
    .pipe(
      z
        .number()
        .int()
        .min(min, `At least ${String(min)}`)
        .max(max, `At most ${String(max)}`),
    );
}

const packageName = z.string().trim().min(1, 'Enter a name').max(100, 'Use at most 100 characters');

export const PLAN_UNITS = ['months', 'days'] as const;
export type PlanUnit = (typeof PLAN_UNITS)[number];

/** The API's cap on a plan's freeze allowance. */
export const MAX_FREEZE_DAYS = 365;

/**
 * Months for a plan (1–120), or days for a day pass (1–31) — the API's limits. A plan may also be
 * frozen for up to `maxFreezeDays` days (blank = 0 = never); a day pass never.
 */
export const membershipPackageFormSchema = z
  .object({
    name: packageName,
    unit: z.enum(PLAN_UNITS),
    length: z.string().trim(),
    price: moneyInputSchema,
    maxFreezeDays: z.string().trim(),
    description: optionalText(2000),
  })
  .superRefine((values, ctx) => {
    const max = values.unit === 'days' ? 31 : 120;
    const count = Number(values.length);
    if (!/^\d+$/.test(values.length) || count < 1 || count > max) {
      ctx.addIssue({
        code: 'custom',
        path: ['length'],
        message: `Enter 1 to ${String(max)} ${values.unit}`,
      });
    }
    const freeze = values.maxFreezeDays;
    if (
      values.unit === 'months' &&
      freeze !== '' &&
      (!/^\d+$/.test(freeze) || Number(freeze) > MAX_FREEZE_DAYS)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['maxFreezeDays'],
        message: `Enter 0 to ${String(MAX_FREEZE_DAYS)} days`,
      });
    }
  })
  .transform(({ unit, length, maxFreezeDays, ...rest }) => ({
    ...rest,
    ...(unit === 'days'
      ? { durationDays: Number(length), maxFreezeDays: 0 }
      : { durationMonths: Number(length), maxFreezeDays: Number(maxFreezeDays || 0) }),
  }));
export type MembershipPackageInput = z.output<typeof membershipPackageFormSchema>;

export const ptPackageFormSchema = z.object({
  name: packageName,
  durationMonths: wholeNumber('months', 1, 120),
  price: moneyInputSchema,
  description: optionalText(2000),
});
export type PtPackageInput = z.output<typeof ptPackageFormSchema>;
